import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZE = 25;

/** Cuts a list into pages and resets to the first one whenever the list changes.
 *
 * Deliberately client-side. These screens search and filter across the whole set, so
 * paging on the server would quietly narrow search to whatever page happened to be
 * loaded. The cost being solved here is rendering: the employees register put 175 rows
 * and 2,564 nodes into one 10,500px page, and that grows with the company.
 */
export function usePaged(items, pageSize = PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(0); }, [total]);
  const current = Math.min(page, pages - 1);
  return {
    page: current, pages, total, pageSize,
    slice: items.slice(current * pageSize, current * pageSize + pageSize),
    setPage,
  };
}

export default function Paginator({ paged, label = "rows", testid = "paginator" }) {
  const { page, pages, total, pageSize, setPage } = paged;
  if (total <= pageSize) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid={testid}>
      <div className="text-xs text-muted-foreground" data-testid={`${testid}-range`}>
        {from}–{to} of {total} {label}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page === 0}
                onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`} aria-label="Previous page">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-xs text-muted-foreground tabular-nums">{page + 1} / {pages}</span>
        <Button variant="outline" size="sm" disabled={page >= pages - 1}
                onClick={() => setPage(page + 1)} data-testid={`${testid}-next`} aria-label="Next page">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
